import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  CalendarClock,
  LayoutList,
  PauseCircle,
  Pencil,
  PlayCircle,
  Plus,
  Trash2,
} from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { monto as comoMonto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"
import type { Planilla } from "@/paginas/interno/GastosProgramados"

type TotalPorDivisa = {
  divisa: string
  ciclo: string
  lineas: number
  activas: number
}

type LineaDePlanilla = {
  id: number
  concepto: string
  gasto: string
  proveedor: string
  monto: string
  divisa: string
  frecuencia: string
  proxima_fecha: string
  activo: boolean
  vencida: boolean
}

type Generada = { periodo: string; orden_id: number; orden: string; vence_en: string }

type Ficha = {
  id: number
  nombre: string
  descripcion: string | null
  activo: boolean
  lineas: LineaDePlanilla[]
  por_divisa: TotalPorDivisa[]
  total_lineas: number
  lineas_activas: number
  lineas_vencidas: number
  proxima_fecha: string | null
  generadas: Generada[]
}

type Resumen = { generadas: string[]; repetidas: number; sin_vencer: number; caducadas: number }

/**
 * Las planillas de programación: agrupaciones de lo que se repite.
 *
 * ⚠ **La planilla AGRUPA y no calcula.** Cada gasto de dentro conserva su calendario,
 * su moneda y su proveedor —el alquiler cae el 5 y el internet el 10—, y al generar
 * **cada uno saca su propia orden en BORRADOR**: una orden de pago admite un solo
 * proveedor y una sola moneda.
 *
 * ⚠ **Planilla con I no es «Plantillas oficiales».** Aquéllas son los formatos
 * impresos del SENIAT y de la agencia; esto es un calendario agrupado. Se parecen al
 * leerlas y no tienen nada que ver.
 *
 * ⚠ **El total es el de UN ciclo, y lo dice la etiqueta.** Con un semanal y un mensual
 * dentro, «lo que cuesta al mes» no es un número único; eso lo da la tarjeta de arriba,
 * que cuenta fecha a fecha.
 *
 * ⚠ **«Generar todas» salta la antelación, no la idempotencia.** Deja la planilla del
 * mes lista aunque falten días; el periodo que ya tiene orden lo rechaza el servidor,
 * así que pulsarlo dos veces no duplica nada.
 */
export function Planillas() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [creando, setCreando] = useState(false)
  const [abierta, setAbierta] = useState<number | null>(null)

  const puedeAdministrar = tienePermiso(usuario, "gasto_programado:administrar")

  const planillas = useQuery({
    queryKey: ["planillas-programacion"],
    queryFn: () => api.get<Planilla[]>("/api/v1/planillas-programacion"),
  })

  const filas = planillas.data ?? []

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <LayoutList size={16} aria-hidden={true} /> Planillas
          </h2>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Lo que se mira y se genera junto. Cada gasto conserva su propio calendario.
          </p>
        </div>
        {puedeAdministrar && (
          <Boton onClick={() => setCreando(true)}>
            <Plus size={16} /> Nueva planilla
          </Boton>
        )}
      </div>

      {planillas.isError && (
        <EstadoError
          titulo="No se pudieron cargar las planillas."
          error={planillas.error}
          onReintentar={() => planillas.refetch()}
        />
      )}

      {/* Con la carga caída la lista está vacía, pero NO es que no haya planillas:
          decirlo debajo del error contradecía al error. */}
      {planillas.isError ? null : filas.length === 0 ? (
        <p className="tarjeta p-3 text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no hay planillas. Una planilla agrupa varios gastos programados —«los fijos de la
          oficina»— para leerlos y generarlos de una vez.
        </p>
      ) : (
        // Una columna desde `lg`: la sección vive en la mitad estrecha, al lado de la
        // agenda. En tableta caben dos; en el teléfono, una.
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
          {filas.map((planilla) => (
            <button
              key={planilla.id}
              type="button"
              onClick={() => setAbierta(planilla.id)}
              className="tarjeta tarjeta-interactiva foco-cristal space-y-1 p-3 text-left"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold">{planilla.nombre}</span>
                {!planilla.activo && <Insignia tono="neutro">Archivada</Insignia>}
              </div>
              {planilla.descripcion !== null && (
                <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  {planilla.descripcion}
                </p>
              )}
              <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                {planilla.lineas} {planilla.lineas === 1 ? "gasto" : "gastos"}
              </p>
            </button>
          ))}
        </div>
      )}

      <Modal
        abierto={creando}
        titulo="Nueva planilla"
        descripcion="Agrupa gastos programados para mirarlos y generarlos juntos. Cada uno conserva su calendario."
        onCerrar={() => setCreando(false)}
      >
        {creando && (
          <FormularioPlanilla
            onCerrar={() => setCreando(false)}
            onGuardado={() => {
              clienteQuery.invalidateQueries({ queryKey: ["planillas-programacion"] })
              setCreando(false)
            }}
          />
        )}
      </Modal>

      <Modal
        abierto={abierta !== null}
        titulo="Planilla de programación"
        descripcion="Lo que agrupa, lo que cuesta cada ciclo y lo que ya redactó."
        onCerrar={() => setAbierta(null)}
        ancho="lg"
      >
        {abierta !== null && (
          <FichaPlanilla planillaId={abierta} onBorrada={() => setAbierta(null)} />
        )}
      </Modal>
    </section>
  )
}

function FormularioPlanilla({
  onCerrar,
  onGuardado,
}: {
  onCerrar: () => void
  onGuardado: () => void
}) {
  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post("/api/v1/planillas-programacion", cuerpo),
    onSuccess: onGuardado,
  })

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const descripcion = String(datos.get("descripcion") ?? "").trim()
    guardar.mutate({
      nombre: String(datos.get("nombre") ?? "").trim(),
      descripcion: descripcion === "" ? null : descripcion,
    })
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <form className="space-y-4" onSubmit={enviar}>
      <Campo
        para="pl-nombre"
        etiqueta="Nombre"
        ayuda="«Los fijos de la oficina», «Servicios del galpón»."
      >
        <input id="pl-nombre" name="nombre" className="campo w-full" required minLength={2} />
      </Campo>
      <Campo para="pl-descripcion" etiqueta="Descripción" ayuda="Opcional.">
        <input id="pl-descripcion" name="descripcion" className="campo w-full" />
      </Campo>
      {error !== null && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Boton onClick={onCerrar}>Cancelar</Boton>
        <Boton type="submit" variante="primario" disabled={guardar.isPending}>
          Crear planilla
        </Boton>
      </div>
    </form>
  )
}

function FichaPlanilla({ planillaId, onBorrada }: { planillaId: number; onBorrada: () => void }) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [editando, setEditando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const puedeGenerar = tienePermiso(usuario, "gasto_programado:generar")
  const puedeAdministrar = tienePermiso(usuario, "gasto_programado:administrar")
  const vista = useRef<HTMLDivElement | null>(null)

  const refrescar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["planillas-programacion"] })
    clienteQuery.invalidateQueries({ queryKey: ["gastos-programados"] })
  }

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.patch(`/api/v1/planillas-programacion/${planillaId}`, cuerpo),
    onSuccess: () => {
      refrescar()
      setEditando(false)
    },
  })

  const borrar = useMutation({
    mutationFn: () => api.delete(`/api/v1/planillas-programacion/${planillaId}`),
    onSuccess: () => {
      refrescar()
      onBorrada()
    },
  })

  const ficha = useQuery({
    queryKey: ["planillas-programacion", planillaId],
    queryFn: () => api.get<Ficha>(`/api/v1/planillas-programacion/${planillaId}`),
  })

  const generar = useMutation({
    mutationFn: (adelantar: boolean) =>
      api.post<Resumen>(
        `/api/v1/planillas-programacion/${planillaId}/generar?adelantar=${adelantar}`,
        {},
      ),
    onSuccess: (r) => {
      setResumen(r)
      clienteQuery.invalidateQueries({ queryKey: ["planillas-programacion"] })
      clienteQuery.invalidateQueries({ queryKey: ["gastos-programados"] })
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
    },
  })

  if (ficha.isPending) return <EstadoCarga etiqueta="Cargando la planilla…" />
  if (ficha.isError || !ficha.data) {
    return (
      <EstadoError
        titulo="No se pudo cargar la planilla."
        error={ficha.error}
        onReintentar={() => ficha.refetch()}
      />
    )
  }

  const datos = ficha.data
  const error =
    [generar.error, guardar.error, borrar.error].find((e) => e instanceof ErrorApi)?.message ?? null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{datos.nombre}</h3>
          {datos.descripcion !== null && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              {datos.descripcion}
            </p>
          )}
          <p className="mt-1 text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
            {datos.lineas_activas} de {datos.total_lineas} activos
            {datos.lineas_vencidas > 0 && ` · ${datos.lineas_vencidas} ya tocan`}
            {datos.proxima_fecha !== null && ` · próxima ${comoFecha(datos.proxima_fecha)}`}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <Exportar
            titulo={`Planilla «${datos.nombre}»`}
            subtitulo={[
              datos.descripcion,
              ...datos.por_divisa.map((t) => `Un ciclo en ${t.divisa}: ${comoMonto(t.ciclo)}`),
            ]
              .filter(Boolean)
              .join(" · ")}
            contenido={vista}
          />
          {puedeAdministrar && (
            <>
              <Boton tamano="sm" onClick={() => setEditando(true)}>
                <Pencil size={14} /> Editar
              </Boton>
              <Boton
                tamano="sm"
                onClick={() => guardar.mutate({ activo: !datos.activo })}
                disabled={guardar.isPending}
              >
                {datos.activo ? (
                  <>
                    <PauseCircle size={14} /> Archivar
                  </>
                ) : (
                  <>
                    <PlayCircle size={14} /> Reactivar
                  </>
                )}
              </Boton>
              <Boton tamano="sm" variante="peligro" onClick={() => setConfirmando(true)}>
                <Trash2 size={14} /> Eliminar
              </Boton>
            </>
          )}
          {puedeGenerar && (
            <>
              <Boton onClick={() => generar.mutate(false)} disabled={generar.isPending}>
                <CalendarClock size={16} /> Generar las que tocan
              </Boton>
              <Boton
                variante="primario"
                onClick={() => generar.mutate(true)}
                disabled={generar.isPending}
              >
                Generar todas
              </Boton>
            </>
          )}
        </div>
      </div>

      {/* ⚠ Por moneda y sin total: sumar bolívares con USDT daría una cifra en una
          unidad que no existe. Y es un CICLO, no un mes. */}
      <div className="flex flex-wrap gap-2">
        {datos.por_divisa.map((total) => (
          <div key={total.divisa} className="tarjeta px-3 py-2">
            <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Un ciclo en {total.divisa}
            </div>
            <div className="text-lg font-semibold tabular-nums">{comoMonto(total.ciclo)}</div>
            <div className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
              {total.activas} de {total.lineas} activos
            </div>
          </div>
        ))}
      </div>

      {editando && (
        <form
          className="tarjeta space-y-3 p-3"
          onSubmit={(e) => {
            e.preventDefault()
            const campos = new FormData(e.currentTarget)
            const descripcion = String(campos.get("descripcion") ?? "").trim()
            guardar.mutate({
              nombre: String(campos.get("nombre") ?? "").trim(),
              descripcion: descripcion === "" ? null : descripcion,
            })
          }}
        >
          <Campo para="pl-ed-nombre" etiqueta="Nombre">
            <input
              id="pl-ed-nombre"
              name="nombre"
              defaultValue={datos.nombre}
              className="campo w-full"
              required
              minLength={2}
            />
          </Campo>
          <Campo para="pl-ed-descripcion" etiqueta="Descripción">
            <input
              id="pl-ed-descripcion"
              name="descripcion"
              defaultValue={datos.descripcion ?? ""}
              className="campo w-full"
            />
          </Campo>
          <div className="flex justify-end gap-2">
            <Boton tamano="sm" onClick={() => setEditando(false)}>
              Cancelar
            </Boton>
            <Boton tamano="sm" type="submit" variante="primario" disabled={guardar.isPending}>
              Guardar
            </Boton>
          </div>
        </form>
      )}

      {confirmando && (
        <div className="tarjeta space-y-2 p-3">
          {/* ⚠ Se dice QUÉ pasa con lo que agrupaba: la pregunta real de quien duda al
              borrar es «¿me llevo por delante los calendarios?». No. */}
          <p className="text-sm">
            ¿Eliminar «{datos.nombre}»? Sus {datos.total_lineas}{" "}
            {datos.total_lineas === 1 ? "gasto queda suelto" : "gastos quedan sueltos"}, con su
            calendario intacto y sin perder lo que ya redactaron. Se va la agrupación, nada más.
          </p>
          <div className="flex justify-end gap-2">
            <Boton tamano="sm" onClick={() => setConfirmando(false)}>
              Cancelar
            </Boton>
            <Boton
              tamano="sm"
              variante="peligro"
              onClick={() => borrar.mutate()}
              disabled={borrar.isPending}
            >
              Eliminar la planilla
            </Boton>
          </div>
        </div>
      )}

      {error !== null && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}
      {resumen !== null && (
        <p className="tarjeta p-3 text-sm">
          {resumen.generadas.length === 0
            ? "No había nada que redactar."
            : `Se redactaron en borrador: ${resumen.generadas.join(", ")}.`}
          {resumen.repetidas > 0 && ` ${resumen.repetidas} ya tenían la orden de su periodo.`}
          {resumen.sin_vencer > 0 && ` ${resumen.sin_vencer} todavía no tocan.`}
        </p>
      )}

      <div ref={vista}>
        <Tabla etiqueta="Gastos de la planilla">
          <Cabecera>
            <Th>Concepto</Th>
            <Th>Proveedor</Th>
            <Th derecha>Monto</Th>
            <Th derecha>Próxima</Th>
            <Th>Estado</Th>
          </Cabecera>
          <Cuerpo>
            {datos.lineas.length === 0 && (
              <TablaVacia colSpan={5}>
                Esta planilla no agrupa nada todavía. Se añade desde el formulario de cada gasto
                programado, eligiéndola en «Planilla».
              </TablaVacia>
            )}
            {datos.lineas.map((linea) => (
              <Fila key={linea.id} atenuada={!linea.activo}>
                <Td>{linea.concepto}</Td>
                <Td>{linea.proveedor}</Td>
                <Td derecha numero>
                  {comoMonto(linea.monto)} {linea.divisa}
                </Td>
                <Td derecha numero>
                  {comoFecha(linea.proxima_fecha)}
                </Td>
                <Td>
                  {!linea.activo ? (
                    <Insignia tono="neutro">Pausada</Insignia>
                  ) : linea.vencida ? (
                    <Insignia tono="aviso">Ya toca</Insignia>
                  ) : (
                    <Insignia tono="exito">Al día</Insignia>
                  )}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      </div>

      {datos.generadas.length > 0 && (
        <div>
          <h4 className="mb-1 text-sm font-semibold">Lo que ya redactó</h4>
          <ul className="space-y-0.5 text-sm">
            {datos.generadas.slice(0, 12).map((g) => (
              <li key={`${g.orden_id}-${g.periodo}`} className="flex flex-wrap gap-x-3">
                <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {g.periodo}
                </span>
                <span>{g.orden}</span>
                <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  venció {comoFecha(g.vence_en)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
